import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';

export interface Toast {
  id: number;
  type: 'success' | 'error';
  message: string;
  leaving?: boolean;
}

// A message that matters (an error) stays long enough to be read twice; a confirmation can go sooner.
const AUTO_DISMISS_MS = { success: 4000, error: 8000 } as const;
const EXIT_ANIM_MS = 300;

interface ToastContextValue {
  toasts: Toast[];
  showToast: (type: 'success' | 'error', message: string) => void;
  dismissToast: (id: number) => void;
}

const ToastContext = createContext<ToastContextValue>({
  toasts: [],
  showToast: () => {},
  dismissToast: () => {},
});

export const useToast = () => useContext(ToastContext);

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const idRef = useRef(0);

  const dismissToast = useCallback((id: number) => {
    // Mark as leaving to play the slide-out, then remove after the animation.
    setToasts(prev => prev.map(t => (t.id === id ? { ...t, leaving: true } : t)));
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
    }, EXIT_ANIM_MS);
  }, []);

  const showToast = useCallback(
    (type: 'success' | 'error', message: string) => {
      const id = ++idRef.current;
      setToasts(prev => [{ id, type, message }, ...prev]);
      setTimeout(() => dismissToast(id), AUTO_DISMISS_MS[type]);
    },
    [dismissToast]
  );

  // Dev-only console helper: window.__toast('success' | 'error', 'message')
  useEffect(() => {
    if (process.env.NODE_ENV !== 'production') {
      (window as any).__toast = showToast;
      return () => { delete (window as any).__toast; };
    }
  }, [showToast]);

  return (
    <ToastContext.Provider value={{ toasts, showToast, dismissToast }}>
      {children}
    </ToastContext.Provider>
  );
};
